import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { NotFoundContent } from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <NotFoundContent />
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);
  const otherCategories = categories.filter((c) => c.slug !== slug);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 邪娠娼馆世界观指南`,
            description: category.description,
            url: `https://hnbocong.cn/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '邪娠娼馆世界观指南',
              url: 'https://hnbocong.cn/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://hnbocong.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: category.name,
                item: `https://hnbocong.cn/category/${slug}`,
              },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: category.name, href: `/category/${slug}` },
              ]}
            />

            <div className="py-8 md:py-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-primary-500/10 border border-primary-500/20">
                  <i className={`${category.icon} text-2xl md:text-3xl text-primary-400`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50">
                    {category.name}
                  </h1>
                  <p className="text-sm md:text-base text-foreground-400 mt-1 max-w-2xl">
                    {category.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-10 flex-wrap">
                {otherCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-4 py-1.5 rounded-full text-xs font-label bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {categoryArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                    <i className="ri-file-list-3-line text-2xl text-foreground-500"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {categoryArticles.map((article) => (
                    <article key={article.slug}>
                      <Link
                        to={`/article/${article.slug}`}
                        className="group flex flex-col sm:flex-row gap-5 p-5 rounded-xl bg-background-100 border border-background-300/30 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                      >
                        <div className="w-full sm:w-48 h-36 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            title={`${article.title} - 邪娠娼馆世界观指南`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                        </div>
                        <div className="flex-1 flex flex-col justify-between min-w-0">
                          <div>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {article.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-xs text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <h2 className="text-base font-heading font-semibold text-foreground-100 mb-2 group-hover:text-primary-400 transition-colors line-clamp-2">
                              {article.title}
                            </h2>
                            <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                              {article.summary}
                            </p>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-foreground-600 mt-3">
                            <span>{article.author}</span>
                            <span>{article.publishDate}</span>
                            <span>{article.readTime}</span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}